import { Controller } from "@hotwired/stimulus"
import { useTransition, useClickOutside } from 'stimulus-use'
import Rails from "@rails/ujs"
import { template, debounce } from "lodash"
import { forceSetValueOnSelect, resolveProductPrices } from "./helpers"

export default class extends Controller {
  static targets = ["container", "input", "list", "product", "resourceTemplate", "outsideClose", "blank", "searchQuerySlot", "item", "missingProductWarning"]

  static values = {
    index: { type: String, default: "_0_" },
    mode: { type: String, default: "modal" },
    fieldPrefix: { type: String, default: "invoice_positions_attributes" },
    // Tryb shared: jeden modal na stronę, otwarty przez openWithParams(), wynik przez event
    shared: { type: Boolean, default: false },
  }

  static outlets = ["invoice"]

  eventCreated = false
  productsData = []
  inputValue = ""
  // Kontekst shared mode: element który otworzył modal (button w wierszu)
  opener = null
  // Parametry wyszukiwania przekazane przez inicjatora (warehouse_id, price_list_id)
  searchParams = {}
  // Numer ostatniego wysłanego wyszukiwania i numer tego, którego wynik jest na liście
  // — patrz komentarz przy Rails.ajax w search()
  searchRequestId = 0
  renderedRequestId = 0

  connect() {
    this.containerTarget.classList.add("hidden")
    this.resourceTemplate = template(this.resourceTemplateTarget.innerHTML)
    useTransition(this, { element: this.containerTarget })
    this.triggerSearch = debounce(this.triggerSearch, 500).bind(this)

    if (this.hasOutsideCloseTarget) {
      useClickOutside(this, {
        element: this.outsideCloseTarget
      })
    }
  }

  // Tryb per-row (desktop/backward compat): otwiera dropdown bez kontekstu
  open() {
    this.toggle()
    this.search()
  }

  // Tryb shared: otwierany przez inicjatora (position-calculator, inline-estimate).
  // opener  — element DOM który wywołał otwarcie (button w wierszu), event wraca do niego
  // params  — { warehouse_id, price_list_id } z invoiceOutlet inicjatora
  // docelowo zostanie tylko tryb shared
  openWithParams(opener, params = {}) {
    this.opener = opener
    this.searchParams = params
    this.inputValue = ""
    if (this.eventCreated !== false) {
      this.inputTarget.value = ""
    }
    this.toggle()
    this.search()
  }

  triggerSearch(event) {
    this.listTarget.innerHTML = ""
    if (this.modeValue === "inline" && event.target.value.length < 2) {
      this.hide()
      return
    }
    this.inputValue = event.target.value
    this.search()
  }

  search() {
    // Fraza zapamiętana na czas żądania: `inputValue` zmienia się przy pisaniu, a etykieta
    // pustego wyniku musi opisywać frazę, dla której przyszły renderowane wiersze.
    const query = this.inputValue
    const input = query.length === 0 ? "*" : query

    const params = {
      rows: 10,
      kind: 'sell',
      name: input
    }

    if (this.sharedValue) {
      // Parametry z inicjatora (nie z invoiceOutlet — dropdown nie zna invoice)
      if (this.searchParams.kind) params["kind"] = this.searchParams.kind
      if (this.searchParams.warehouse_id) params["warehouse_id"] = this.searchParams.warehouse_id
      if (this.searchParams.price_list_id) params["price_list_id"] = this.searchParams.price_list_id
    } else if (this.hasInvoiceOutlet) {
      // Tryb per-row: parametry z invoiceOutlet (zachowanie dotychczasowe)
      params["kind"] = this.invoiceOutlet.incomeValue ? "sell" : "buy"
      if (this.invoiceOutlet.warehouseIdValue !== 0) {
        params["warehouse_id"] = this.invoiceOutlet.warehouseIdValue
      }
      if (this.invoiceOutlet.hasPriceListTarget) {
        params["price_list_id"] = this.invoiceOutlet.priceListTarget.value
      }
    }

    // Otwarcie dropdownu odpala wyszukiwanie "*", a wpisywanie nazwy kolejne (debounce 500 ms),
    // więc kilka odpowiedzi bywa w locie naraz i wracają w dowolnej kolejności. Lista idzie
    // wyłącznie w stronę nowszych danych: renderujemy odpowiedź nowszą od tej, która jest już
    // na liście, a starszą pomijamy — inaczej spóźniona odpowiedź pokazywałaby wynik dla
    // nieaktualnej frazy (a przy pokrywających się wynikach ten sam produkt dwa razy).
    // Warunkiem jest „nowsza od wyrenderowanej", nie „najświeższa wysłana": podpowiedzi mają
    // się odświeżać także w trakcie pisania, a wynik żądania pośredniego jest już policzony.
    const requestId = ++this.searchRequestId

    Rails.ajax({
      url: "/products/find",
      type: 'post',
      data: new URLSearchParams(params),
      success: (data) => {
        if (requestId <= this.renderedRequestId) return

        this.renderedRequestId = requestId
        this.showResults(data, query)
      },
      error: (err) => console.error("Błąd w autocomplete", err)
    })
  }

  showResults(data, query = this.inputValue) {
    const hasResults = data.rows.length > 0

    if (this.modeValue === "inline") {
      this.renderItems(data)
      if (this.hasSearchQuerySlotTarget) {
        this.searchQuerySlotTarget.textContent = query
      }
      const hideResult = !hasResults && !this.hasBlankTarget
      this.containerTarget.classList.toggle("hidden", hideResult)
    } else {
      this.renderItems(data)
      this.listTarget.classList.toggle("hidden", !hasResults)

      if (this.hasBlankTarget) {
        this.blankTarget.classList.toggle("hidden", hasResults)
      }
    }
  }

  // Jedno przypisanie całej listy: zawiera DOKŁADNIE wynik renderowanego żądania (pusty wynik
  // ją czyści), a `key` pozostaje zgodny z indeksem w `productsData`, po którym `select`
  // odczytuje kliknięty produkt. Doklejanie po jednej pozycji jest wykluczone — setter
  // innerHTML re-parsuje całe poddrzewo, więc każda iteracja niszczyłaby i odtwarzała
  // wcześniej wstawione węzły (Stimulus wiązałby `data-action` od nowa, a widoczny element
  // mógł zniknąć w trakcie i zgubić kliknięcie).
  renderItems(data) {
    this.productsData = data.rows
    this.listTarget.innerHTML = data.rows
      .map((product, index) => this.resourceTemplate({ key: index, label: product.label }))
      .join("")
  }

  select(event) {
    event.preventDefault()
    const productIndex = event.params.value
    const rawProduct = this.productsData[productIndex]

    if (this.sharedValue) {
      // Shared mode: wyślij event do inicjatora, niech sam wypełni pola
      this.opener.dispatchEvent(new CustomEvent("dropdown-invoice-product:selected", {
        detail: { product: rawProduct },
        bubbles: true,
      }))
      this.isProductExist()
      this.hide()
      return
    }

    // Tryb per-row (backward compat) — dotychczasowa logika wypełniania pól
    const product = this.hasInvoiceOutlet ? resolveProductPrices(rawProduct, this.invoiceOutlet.incomeValue) : rawProduct
    const prefix = this.fieldPrefixValue + this.indexValue
    let showMore = false

    if (this.hasProductTarget) {
      this.productTarget.value = product.value
    } else {
      this.inputTarget.value = product.value
    }
    document.getElementById(prefix + "product_id").value = product.id
    if (product.quantity) document.getElementById(prefix + "quantity").value = product.quantity

    if (document.getElementById(prefix + "quantity_unit")) {
      const unit = product.quantity_unit
      if (unit) {
        forceSetValueOnSelect(document.getElementById(prefix + "quantity_unit"), unit)
      } else {
        document.getElementById(prefix + "quantity_unit").value = ""
      }
      document.getElementById(prefix + "quantity_unit").dispatchEvent(new Event("change", { bubbles: true }))
    }

    const priceNetEl = document.getElementById(prefix + "price_net")
    const priceGrossEl = document.getElementById(prefix + "price_gross")
    const totalPriceGrossEl = document.getElementById(prefix + "total_price_gross")
    const useGross = this.invoiceOutlet.priceSetupValue.price_kind === "gross"
    const preferredEl = useGross ? priceGrossEl : priceNetEl
    const el = preferredEl || totalPriceGrossEl

    let triggerEl = null
    if (el) {
      el.value = useGross ? product.price_gross : product.price_net
      triggerEl = el
    }

    let taxEl = null
    if (this.hasInvoiceOutlet && this.invoiceOutlet.taxVisibleValue && !["wdt", "export_products"].includes(this.invoiceOutlet.kindValue)) {
      if (this.invoiceOutlet.autocompleteTax) {
        // na dokumentach z wymuszoną stawką (RR: ryczałt) stawka z karty produktu jej nie nadpisuje
        const forcedTax = this.invoiceOutlet.forcedPositionTaxValue
        if (!forcedTax) this.invoiceOutlet.setDefaultTax()
        const itemTax = forcedTax || this.invoiceOutlet.defaultTax || product.tax
        taxEl = document.getElementById(prefix + "tax")
        const mossOn = this.invoiceOutlet.hasMossOutlet && this.invoiceOutlet.mossOutlet.useMossTarget.checked
        const hasMatchingOption = !taxEl?.matches("select") || Array.from(taxEl.options).some((option) => option.value === itemTax)
        const hasOwnTax = itemTax !== undefined && itemTax !== null && itemTax !== ""
        if (hasOwnTax && (!mossOn || hasMatchingOption)) {
          forceSetValueOnSelect(taxEl, itemTax)
        }
      }
    } else if (this.hasInvoiceOutlet && !this.invoiceOutlet.taxVisibleValue && this.invoiceOutlet.accountCountryValue === "fr") {
      taxEl = document.getElementById(prefix + "tax")
      forceSetValueOnSelect(taxEl, "disabled")
    }

    if (product.description?.length > 0 && document.getElementById(prefix + "description")) {
      document.getElementById(prefix + "description").value = product.description
      showMore = true
    }

    if (product.code?.length > 0 && document.getElementById(prefix + "code")) {
      document.getElementById(prefix + "code").value = product.code
      showMore = true
    }

    if (product.gtu_codes?.length > 0 && document.getElementById(prefix + "gtu_code")?.length > 0) {
      document.getElementById(prefix + "gtu_code").value = product.gtu_codes[0]
      showMore = true
    }

    if (product.additional_info?.length > 0 && document.getElementById(prefix + "additional_info")) {
      document.getElementById(prefix + "additional_info").value = product.additional_info
      showMore = true
    }

    if (showMore && document.querySelector("[data-toggle-position-button]")) {
      document.querySelector("[data-toggle-position-button]").dispatchEvent(new Event("click"))
    }

    if (triggerEl) triggerEl.dispatchEvent(new Event("change"))
    if (taxEl) taxEl.dispatchEvent(new Event("change"))

    if (this.hasInvoiceOutlet && this.invoiceOutlet.handleCondNoteValue) {
      this.invoiceOutlet.updateCondNote()
    }

    this.isProductExist()
    this.hide()
  }

  add() {
    if (this.sharedValue) {
      // Shared mode: przekaż wpisaną nazwę do inicjatora, bez produktu z bazy
      this.opener.dispatchEvent(new CustomEvent("dropdown-invoice-product:selected", {
        detail: { product: { value: this.inputValue, id: null } },
        bubbles: true,
      }))
      this.hide()
      return
    }

    // Tryb per-row (backward compat)
    if (this.modeValue === "inline") {
      document.getElementById(`${this.fieldPrefixValue}${this.indexValue}product_id`).value = ""
      this.clearForm()
      this.inputTarget.value = this.inputValue
    } else {
      this.productTarget.value = this.inputTarget.value
    }
    this.hide()
  }

  isProductExist() {
    if (this.hasMissingProductWarningTarget) {
      if (this.inputTarget.value !== "" && document.getElementById(`${this.fieldPrefixValue}${this.indexValue}product_id`).value.length === 0) {
        this.missingProductWarningTarget.classList.remove("hidden")
      } else {
        this.missingProductWarningTarget.classList.add("hidden")
      }
    }
  }

  clearForm() {
    document.querySelectorAll(`input[type="text"][id^=${this.fieldPrefixValue}${this.indexValue}`).forEach((elem) => { elem.value = null })
  }

  toggle() {
    this.toggleTransition()
    if (this.eventCreated === false) {
      this.inputTarget.value = ""
      this.inputTarget.focus()

      if (this.hasOutsideCloseTarget) {
        const fn = this.clickOutside.bind(this)
        this.eventCreated = fn
        document.addEventListener("click", fn)
      }
    } else {
      this.removeEvent()
    }
  }

  clickOutside() {
    if (this.hasContainerTarget && !this.containerTarget.classList.contains("hidden")) {
      this.hide()
    }
  }

  hide() {
    this.leave()
    this.removeEvent()
    if (this.hasContainerTarget) this.containerTarget.classList.add("hidden")
    this.listTarget.innerHTML = ""
  }

  removeEvent() {
    if (this.eventCreated !== false) {
      document.removeEventListener("click", this.eventCreated)
      this.eventCreated = false
    }
  }

  disconnect() {
    this.removeEvent()
  }
}
